import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { Order } from 'src/app/common/Order';
import { ApiServiceBase } from './ApiServiceBase';
import { defaultDateRange, toApiDate } from '../common/date-helpers';
import { OrderPositionStatus, OrderPositionStatusConstants } from '../common/OrderPositionStatus';
import { OrderGroup } from '../common/OrderGroup';
import { withNormalisedReservationStatus } from '../common/ReservationStatus';
import { ClaimOrderModel } from '../common/ClaimOrderModel';
import { OrderFilter } from '../common/OrderFilter';
import { OrderClaimFilter } from '../common/OrderClaimFilter';
import { PagedResult, PagingParams, pagingToHttpParams } from '../common/PagedResult';
import { AppProfileService } from './profile.service';

@Injectable({
  providedIn: 'root',
})
export class OrderService extends ApiServiceBase {
  private readonly claimsChanged$ = new BehaviorSubject<void>(undefined);

  /**
   * The number on the „Reklamationen" badge: positions awaiting a cancellation decision.
   *
   * One request per signed-in retailer and per claim change, shared by every nav that shows the
   * badge (#199). Each `my-account-nav` used to fetch on its own — twice per profile emission,
   * once directly and once through the replaying change subject — and stacked a further
   * subscription on every emission; with the header and the sidebar both rendering one that was
   * six requests per page load, for customers who are no retailer too, who never see the badge.
   * A failed count shows no badge rather than ending the stream.
   */
  public readonly pendingClaimsCount$: Observable<number> = combineLatest([
    inject(AppProfileService).currentProfile$.pipe(
      map((p) => (p?.customer?.isRetailer ? p.id : null)),
      distinctUntilChanged(),
    ),
    this.claimsChanged$,
  ]).pipe(
    switchMap(([retailerProfileId]) =>
      retailerProfileId
        ? this.getSaleOrdersByFilter(
            null,
            null,
            null,
            [OrderPositionStatusConstants.PositionStatusIdCancellationPending],
            { skip: 0, take: 1 },
          ).pipe(
            // #648: the search is paged — the badge needs the filtered total, not the page length.
            map((r) => r.totalCount),
            catchError(() => of(0)),
          )
        : of(0),
    ),
    // No refCount: the shell destroys and re-creates the navs while it starts up, and a count
    // that reset whenever the last one went away would be fetched again by the next.
    shareReplay(1),
  );

  protected readonly path = 'order';

  getDefaultFilter(): OrderFilter {
    const { from, to } = defaultDateRange();
    return new OrderFilter(from, to);
  }

  getDefaultOrderClaimFilter(): OrderFilter {
    const { from, to } = defaultDateRange();
    return new OrderClaimFilter(null, from, to);
  }

  getById(id: string): Observable<Order> {
    return this.http
      .get<Order>(this.getUrl() + '/' + id)
      .pipe(take(1));
  }

  getSaleOrdersByFilter(
    dateFrom?: Date,
    dateTo?: Date,
    customerId?: string,
    states?: string[],
    paging?: PagingParams,
  ): Observable<PagedResult<Order>> {
    return this.http
      .get<PagedResult<Order>>(this.getUrl() + '/searchsaleorders', {
        params: {
          dateFrom: toApiDate(dateFrom),
          dateTo: toApiDate(dateTo),
          recipientCustomerId: customerId == null ? '' : customerId,
          states: states ? states : null,
          ...pagingToHttpParams(paging),
        },
      });
  }

  getPurchaseOrderGroupsById(orderGroupId: string): Observable<OrderGroup> {
    return this.http
      .get<OrderGroup>(this.getUrl() + '/purchaseordergroup/' + orderGroupId)
      .pipe(map(withNormalisedReservationStatus));
  }

  getPurchaseOrderGroupsByFilter(
    dateFrom?: Date,
    dateTo?: Date,
    states?: number[],
    paging?: PagingParams,
  ): Observable<PagedResult<OrderGroup>> {
    return this.http
      .get<PagedResult<OrderGroup>>(this.getUrl() + '/searchpurchaseordergroups', {
        params: {
          dateFrom: toApiDate(dateFrom),
          dateTo: toApiDate(dateTo),
          states: states ? states : null,
          ...pagingToHttpParams(paging),
        },
      })
      .pipe(
        map((page) => ({
          ...page,
          items: (page?.items ?? []).map(withNormalisedReservationStatus),
        })),
      );
  }

  getBillableOrders(
    customerNameOrNumber: string,
    dateFrom?: Date,
    dateTo?: Date,
    paging?: PagingParams,
  ): Observable<PagedResult<Order>> {
    return this.http
      .get<PagedResult<Order>>(this.getUrl() + '/billableorders', {
        params: {
          dateFrom: toApiDate(dateFrom),
          dateTo: toApiDate(dateTo),
          customerNameOrNumber: customerNameOrNumber == null ? '' : customerNameOrNumber,
          ...pagingToHttpParams(paging),
        },
      });
  }

  updateForInvoiceReady(_id: string, order: Order): Observable<Order> {
    return this.http
      .put<Order>(this.getUrl() + '/setinvoiceready', order);
  }

  claimOrderPositions(claimOrder: ClaimOrderModel): Observable<Order> {
    return this.http
      .put<Order>(this.getUrl() + '/claim', claimOrder)
      .pipe(
        tap(() => this.pendingClaimsChanged()),
      );
  }

  approveOrderPositionClaims(claimOrder: ClaimOrderModel): Observable<Order> {
    return this.http
      .put<Order>(this.getUrl() + '/approve', claimOrder)
      .pipe(
        tap(() => this.pendingClaimsChanged()),
      );
  }

  rejectOrderPositionClaims(claimOrder: ClaimOrderModel): Observable<Order> {
    return this.http
      .put<Order>(this.getUrl() + '/reject', claimOrder)
      .pipe(
        tap(() => this.pendingClaimsChanged()),
      );
  }

  /**
   * Records that the order's receipt was printed (#205): the backend stamps the order's
   * `lastPrintDateTime`, which marks every later print a copy. No body — the id in the path is
   * all the endpoint reads; this used to send a cart to `order/print`, which nothing serves.
   */
  markPrinted(orderId: string): Observable<void> {
    return this.http.put<void>(`${this.getUrl()}/print/${orderId}`, null);
  }

  public getOrderPositionStates(): OrderPositionStatus[] {
    return [
      {
        Id: OrderPositionStatusConstants.PositionStatusIdDefault,
        DisplayName: OrderPositionStatusConstants.PositionStatusDisplayNameDefault,
      },
      {
        Id: OrderPositionStatusConstants.PositionStatusIdCancellationPending,
        DisplayName: OrderPositionStatusConstants.PositionStatusDisplayNameCancellationPending,
      },
      {
        Id: OrderPositionStatusConstants.PositionStatusIdCancelled,
        DisplayName: OrderPositionStatusConstants.PositionStatusDisplayNameCancelled,
      },
    ];
  }

  private pendingClaimsChanged(): void {
    this.claimsChanged$.next();
  }
}
